import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Button, Table, Typography, Space, Tooltip, message, Alert } from 'antd';
import { ProductOutlined, AmazonOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import api from '../../../services/api';

const { Text } = Typography;

const FulfillToPrinterModal = ({
    visible,
    onCancel,
    onConfirm,
    selectedOrders,
    loading: externalLoading,
    platform = 'amazon'
}) => {
    const [internalLoading, setInternalLoading] = useState(false);
    
    // Use internal loading state or external loading state
    const isLoading = internalLoading || externalLoading;

    // Add CSS for error rows
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .fulfill-to-printer-modal .error-row {
                background-color: #fff2f0 !important;
            }
            .fulfill-to-printer-modal .error-row:hover {
                background-color: #ffe7e6 !important;
            }
            .fulfill-to-printer-modal .error-row td {
                border-bottom: 1px solid #ffccc7;
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    // Validate orders and categorize them
    const { validOrders, invalidOrders } = useMemo(() => {
        const valid = [];
        const invalid = [];

        selectedOrders.forEach(order => {
            let hasError = false;
            let errorReasons = [];

            // Check if printer is assigned
            if (!order.printerName || order.printerName === 'No printer assigned') {
                hasError = true;
                errorReasons.push('No printer');
            }

            // Check shipping name (required from order.customerName which maps to shipName)
            if (!order.customerName || order.customerName === 'N/A') {
                hasError = true;
                errorReasons.push('No name');
            }

            // Check shipping address information
            if (!order.shipAddressLine1 || !order.shipCity) {
                hasError = true;
                errorReasons.push('No address');
            }

            // Check customization status - must be one of the valid values
            const validCustomizationStatuses = ['CustomerConfirmed', 'ConfirmedNoReply', 'AutoFulfill', 'NoCustomization'];
            if (!order.customizationStatus || !validCustomizationStatuses.includes(order.customizationStatus)) {
                hasError = true;
                errorReasons.push('Invalid status');
            }

            if (hasError) {
                invalid.push({
                    ...order,
                    errorReason: errorReasons.join('; ')
                });
            } else {
                valid.push(order);
            }
        });

        return { validOrders: valid, invalidOrders: invalid };
    }, [selectedOrders]);

    const allOrders = [...validOrders, ...invalidOrders];
    const hasInvalidOrders = invalidOrders.length > 0;

    const handleConfirm = async () => {
        try {
            // Set internal loading to true immediately when button is clicked
            setInternalLoading(true);
            
            const normalizedPlatform = (platform || 'amazon').toString().trim().toLowerCase();

            // Extract platform item IDs from valid orders only
            const itemIds = validOrders
                .map(order => order.orderItemNumber)
                .filter(Boolean);
            
            // Call the fulfill to printer API
            const response = await api.post('/orders/fulfill-to-printer', {
                platform: normalizedPlatform,
                itemIds
            });

            if (response.data.success) {
                // Call the parent's onConfirm to handle UI updates
                onConfirm(response.data);
            } else {
                throw new Error(response.data.error || 'Failed to fulfill to printer');
            }
        } catch (error) {
            console.error('Error fulfilling to printer:', error);
            message.error('Failed to fulfill to printer. Please try again.');
            // Don't throw error here to prevent unhandled promise rejection
        } finally {
            // Always reset internal loading state
            setInternalLoading(false);
        }
    };

    const normalizedPlatform = useMemo(() => (platform || 'amazon').toString().trim().toLowerCase(), [platform]);

    const orderIdIcon = useMemo(() => {
        if (normalizedPlatform === 'amazon') {
            return <AmazonOutlined style={{ color: '#FF9900', fontSize: 12 }} />;
        }
        return null;
    }, [normalizedPlatform]);

    const columns = [
        {
            title: '#',
            key: 'index',
            width: 50,
            align: 'center',
            render: (_, record, index) => (
                <Text style={{ fontSize: 12, fontWeight: 'bold' }}>{index + 1}</Text>
            )
        },
        {
            title: 'Order ID',
            dataIndex: 'orderNumber',
            key: 'orderNumber',
            width: 190,
            render: (text) => (
                <Space>
                    {orderIdIcon}
                    <Text style={{ fontSize: 12 }}>{text}</Text>
                </Space>
            )
        },
        {
            title: 'Order Item ID',
            dataIndex: 'orderItemNumber',
            key: 'orderItemNumber',
            width: 160,
            render: (text) => <Text style={{ fontSize: 12 }}>{text}</Text>
        },
        {
            title: 'SKU',
            dataIndex: 'sku',
            key: 'sku',
            width: 270,
            render: (text) => <Text style={{ fontSize: 12, fontFamily: 'monospace' }}>{text}</Text>
        },
        {
            title: 'Product Name',
            dataIndex: 'title',
            key: 'title',
            ellipsis: true,
            render: (text) => (
                <Tooltip title={text}>
                    <Text style={{ fontSize: 12 }}>{text}</Text>
                </Tooltip>
            )
        },
        {
            title: 'Printer',
            dataIndex: 'printerName',
            key: 'printerName',
            width: 140,
            render: (text, record) => {
                const hasError = record.errorReason && record.errorReason.includes('No printer');
                return (
                    <Text style={{ 
                        fontSize: 12, 
                        fontWeight: 'bold', 
                        color: hasError ? '#ff4d4f' : '#52c41a' 
                    }}>
                        {text || 'No printer assigned'}
                    </Text>
                );
            }
        },
        {
            title: 'Customer / Shipping',
            dataIndex: 'customerName',
            key: 'customerName',
            width: 160,
            render: (text, record) => {
                const hasNameError = record.errorReason && record.errorReason.includes('No name');
                const hasAddressError = record.errorReason && record.errorReason.includes('No address');
                const hasError = hasNameError || hasAddressError;
                
                return (
                    <div>
                        <Text style={{ 
                            fontSize: 12,
                            color: hasNameError ? '#ff4d4f' : 'inherit',
                            fontWeight: hasNameError ? 'bold' : 'normal'
                        }}>
                            {text}
                        </Text>
                        {hasAddressError && (
                            <div style={{ fontSize: 10, color: '#ff4d4f', marginTop: 2 }}>
                                No address
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            title: 'Customization Status',
            dataIndex: 'customizationStatus',
            key: 'customizationStatus',
            width: 160,
            render: (text, record) => {
                const hasError = record.errorReason && record.errorReason.includes('Invalid status');
                const validStatuses = ['CustomerConfirmed', 'ConfirmedNoReply', 'AutoFulfill', 'NoCustomization'];
                const isValid = validStatuses.includes(text);
                
                if (hasError) {
                    return (
                        <Text type="danger" style={{ fontSize: 11 }}>
                            <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                            Invalid
                        </Text>
                    );
                } else {
                    return (
                        <Text style={{
                            fontSize: 12,
                            color: isValid ? '#52c41a' : '#ff4d4f',
                            fontWeight: isValid ? 'normal' : 'bold'
                        }}>
                            {text || 'N/A'}
                        </Text>
                    );
                }
            }
        }
    ];

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ProductOutlined style={{ color: '#52c41a' }} />
                    <span>Fulfill to Printer Confirmation</span>
                </div>
            }
            open={visible}
            onCancel={onCancel}
            footer={[
                <Button key="cancel" onClick={onCancel} disabled={isLoading}>
                    Cancel
                </Button>,
                <Button 
                    key="submit"
                    variant='solid'
                    color='green'
                    icon={<ProductOutlined />}
                    onClick={handleConfirm}
                    loading={isLoading}
                    disabled={hasInvalidOrders || validOrders.length === 0}
                >
                    Fulfill to Printer ({validOrders.length} items)
                </Button>
            ]}
            width='85%'
            className="fulfill-to-printer-modal"
            styles={{
                body: {
                    padding: '24px'
                }
            }}
        >
            <div style={{ marginBottom: 16 }}>
                <Text>Are you sure you want to fulfill the following order items to their assigned printers?</Text>
                <br />
                <Text type="secondary">This action will send the orders to production.</Text>
            </div>

            {hasInvalidOrders && (
                <Alert
                    message="Some items cannot be fulfilled"
                    description={`${invalidOrders.length} item(s) have validation errors. Check: printer assignment, shipping info, and customization status.`}
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}
            
            <Table
                dataSource={allOrders}
                rowKey="id"
                pagination={false}
                size="small"
                columns={columns}
                scroll={{ y: 400 }}
                rowClassName={(record) => {
                    if (record.errorReason) {
                        return 'error-row';
                    }
                    return '';
                }}
            />
            
            <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f6ffed', borderRadius: 6 }}>
                <Text strong style={{ color: '#52c41a' }}>
                    Valid Items: {validOrders.length}
                </Text>
                {hasInvalidOrders && (
                    <>
                        <Text style={{ marginLeft: 16 }} strong type="danger">
                            Invalid Items: {invalidOrders.length}
                        </Text>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default FulfillToPrinterModal;